/// <reference types="vite/client" />
import { useEffect, useRef } from "react";
import type { LocalVideoTrack } from "twilio-video";
import { useVideoCalling } from "./useVideoCallingContext";
import type { VideoCallingSlice } from "../types";

type ProcessorsModule = typeof import("@twilio/video-processors");
type GaussianBlurBackgroundProcessor = InstanceType<ProcessorsModule["GaussianBlurBackgroundProcessor"]>;

let modulePromise: Promise<ProcessorsModule> | null = null;
const loadVideoProcessors = (): Promise<ProcessorsModule> => {
  if (!modulePromise) {
    modulePromise = import("@twilio/video-processors");
  }
  return modulePromise;
};

const detachProcessor = (track: LocalVideoTrack | null, processor: GaussianBlurBackgroundProcessor) => {
  if (!track) return;
  try {
    track.removeProcessor(processor);
  } catch {
    // Detaching from a stopped/disposed track is a no-op
  }
};

/**
 * Manages the lifecycle of Twilio's Gaussian background blur processor.
 *
 * Lazy-imports `@twilio/video-processors` once on mount, probes browser support,
 * holds a single processor instance in a ref, and synchronizes the processor
 * attachment with `[localVideoTrack, isBlurEnabled, isBlurSupported]`. Re-attaches
 * automatically when the user switches camera devices mid-call.
 *
 * Should be called exactly once per call window (mount it in `VideoCallWindow`).
 */
export const useBackgroundBlur = () => {
  const videoCalling = useVideoCalling();

  const videoCallingRef = useRef<VideoCallingSlice>(videoCalling);
  videoCallingRef.current = videoCalling;

  const processorRef = useRef<GaussianBlurBackgroundProcessor | null>(null);
  const modelLoadedRef = useRef(false);
  const previousTrackRef = useRef<LocalVideoTrack | null>(null);

  const { localVideoTrack, isBlurEnabled, isBlurSupported } = videoCalling;

  // Lazy-load the library once and probe support.
  useEffect(() => {
    let cancelled = false;
    loadVideoProcessors()
      .then((mod) => {
        if (cancelled) return;
        videoCallingRef.current.setBlurSupported(mod.isSupported);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load @twilio/video-processors:", error);
        videoCallingRef.current.setBlurSupported(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync the processor with the current track + preference.
  useEffect(() => {
    if (!isBlurSupported) return;

    const vc = videoCallingRef.current;
    let cancelled = false;

    // If the active track changed, detach from the previous one.
    if (previousTrackRef.current && previousTrackRef.current !== localVideoTrack && processorRef.current) {
      detachProcessor(previousTrackRef.current, processorRef.current);
    }
    previousTrackRef.current = localVideoTrack;

    if (isBlurEnabled && localVideoTrack) {
      const apply = async () => {
        try {
          const mod = await loadVideoProcessors();
          if (cancelled) return;

          if (!processorRef.current) {
            const baseUrl = import.meta.env.BASE_URL ?? "/";
            processorRef.current = new mod.GaussianBlurBackgroundProcessor({
              assetsPath: `${baseUrl}twilio-video-processors`,
            });
          }

          if (!modelLoadedRef.current) {
            vc.setBlurLoading(true);
            await processorRef.current.loadModel();
            modelLoadedRef.current = true;
            vc.setBlurLoading(false);
          }
          if (cancelled) return;

          // Don't re-attach if the track already has our processor.
          if (localVideoTrack.processor === processorRef.current) return;

          localVideoTrack.addProcessor(processorRef.current, {
            inputFrameBufferType: "videoframe",
            outputFrameBufferContextType: "bitmaprenderer",
          });
        } catch (error) {
          console.error("Failed to apply background blur:", error);
          vc.setBlurLoading(false);
          vc.setBlurEnabled(false);
        }
      };
      apply();
    } else if (processorRef.current && localVideoTrack && localVideoTrack.processor === processorRef.current) {
      detachProcessor(localVideoTrack, processorRef.current);
    }

    return () => {
      cancelled = true;
    };
  }, [localVideoTrack, isBlurEnabled, isBlurSupported]);

  // Final cleanup on unmount: detach from whatever track is current.
  useEffect(() => {
    return () => {
      if (processorRef.current && previousTrackRef.current) {
        detachProcessor(previousTrackRef.current, processorRef.current);
      }
      processorRef.current = null;
      previousTrackRef.current = null;
    };
  }, []);
};
